<template>
    <div class="menu-toast-stack" role="status" aria-live="polite">
        <transition-group name="menu-toast" tag="div">
            <div v-for="toast in toasts" v-bind:key="toast.id"
                 class="menu-toast animated bounceInRight"
                 v-bind:class="isDisabled(toast) ? 'menu-toast-off' : 'menu-toast-on'"
                 v-on:click="open(toast)">
                <div class="menu-toast-accent"></div>
                <div class="menu-toast-body">
                    <button type="button" class="menu-toast-close"
                            v-on:click.stop="dismiss(toast.id)" aria-label="Dismiss">&times;</button>
                    <div class="menu-toast-head">
                        <i class="fa" v-bind:class="isDisabled(toast) ? 'fa-ban' : 'fa-check-circle'"></i>
                        {{ heading(toast) }}
                    </div>
                    <h4 class="menu-toast-message">{{ toast.message }}</h4>
                    <div class="menu-toast-meta">
                        <div><i class="fa fa-building-o"></i> {{ toast.restaurant_name || 'Unknown restaurant' }}</div>
                        <div><i class="fa fa-map-marker"></i> {{ toast.branch_area || 'Unknown area' }}</div>
                    </div>
                    <div class="menu-toast-foot">
                        <span class="menu-toast-source">{{ sourceLabel(toast) }}</span>
                        <span class="menu-toast-time">{{ relativeTime(toast) }}</span>
                        <span class="menu-toast-cta">View menu alerts <i class="fa fa-angle-right"></i></span>
                    </div>
                </div>
            </div>
        </transition-group>
    </div>
</template>

<script>
    /* eslint-disable */

    /**
     * The portal-wide menu availability popup (an item or serving was switched on/off),
     * top right, on any page. Mirrors LiveOrderToast.
     *
     * Positioning is owned by the `.portal-toast-column` wrapper in App.vue, so this root
     * is a plain block and must not set position:fixed (that would stack it on top of the
     * order toast instead of below it).
     *
     * The store decides whether a toast is pushed at all; nothing is queued while the
     * Menu Alerts page is open.
     */
    const AUTO_DISMISS_MS = 12000

    export default {
        name: "MenuAvailabilityToast",
        data() {
            return {
                timers: {}
            }
        },
        computed: {
            toasts() {
                return this.$store.state.menuAlerts.toasts;
            }
        },
        watch: {
            toasts(list) {
                list.forEach(toast => {
                    if (this.timers[toast.id]) return;
                    this.timers[toast.id] = setTimeout(() => this.dismiss(toast.id), AUTO_DISMISS_MS);
                });
            }
        },
        methods: {
            isDisabled(toast) {
                return toast.action === 'DISABLED';
            },
            heading(toast) {
                if (toast.action === 'DISABLED') {
                    return toast.target_type === 'SERVING' ? 'Serving unavailable' : 'Item unavailable';
                }
                return 'Back in stock';
            },
            sourceLabel(toast) {
                if (toast.source === 'KITCHEN') return 'Kitchen';
                if (toast.source === 'PORTAL') return 'Portal';
                return toast.source || '';
            },
            relativeTime(toast) {
                return toast.occurred_at ? this.$moment(toast.occurred_at).fromNow() : '';
            },
            dismiss(id) {
                if (this.timers[id]) {
                    clearTimeout(this.timers[id]);
                    delete this.timers[id];
                }
                this.$store.commit('menuAlerts/dismissToast', id);
            },
            open(toast) {
                this.dismiss(toast.id);
                if (this.$route.path !== '/menu_alerts') {
                    this.$router.push('/menu_alerts');
                }
            }
        },
        beforeDestroy() {
            Object.keys(this.timers).forEach(id => clearTimeout(this.timers[id]));
            this.timers = {};
        }
    }
</script>

<style scoped>
    .menu-toast {
        position: relative;
        display: flex;
        width: 100%;
        margin-bottom: 10px;
        background: #ffffff;
        border: 1px solid #e7eaec;
        border-radius: 3px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, .18);
        cursor: pointer;
        overflow: hidden;
        animation-duration: .5s;
    }

    .menu-toast:hover {
        box-shadow: 0 6px 22px rgba(0, 0, 0, .26);
    }

    /* Red when something was switched off, brand green when it is back. */
    .menu-toast-accent {
        width: 5px;
        flex: 0 0 5px;
    }

    .menu-toast-off .menu-toast-accent { background: #ed5565; }
    .menu-toast-on .menu-toast-accent { background: #1ab394; }

    .menu-toast-body {
        padding: 12px 28px 10px 12px;
        flex: 1 1 auto;
        min-width: 0;
    }

    .menu-toast-close {
        position: absolute;
        top: 4px;
        right: 8px;
        border: 0;
        background: none;
        font-size: 18px;
        line-height: 1;
        color: #b0b4b6;
        padding: 2px 4px;
        cursor: pointer;
    }

    .menu-toast-close:hover,
    .menu-toast-close:focus { color: #676a6c; }

    .menu-toast-head {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .4px;
        text-transform: uppercase;
        margin-bottom: 4px;
    }

    .menu-toast-off .menu-toast-head { color: #ed5565; }
    .menu-toast-on .menu-toast-head { color: #1ab394; }

    .menu-toast-message {
        margin: 0 0 6px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.35;
        color: #676a6c;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }

    .menu-toast-meta {
        font-size: 12px;
        color: #676a6c;
        line-height: 1.7;
    }

    .menu-toast-meta i {
        width: 14px;
        color: #9a9da0;
    }

    .menu-toast-foot {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 8px;
        padding-top: 7px;
        border-top: 1px solid #f1f3f4;
        font-size: 11px;
    }

    .menu-toast-source {
        padding: 1px 6px;
        margin-right: 8px;
        border-radius: 3px;
        background: #f3f3f4;
        color: #676a6c;
        font-weight: 600;
    }

    .menu-toast-time {
        color: #9a9da0;
        flex: 1 1 auto;
    }

    .menu-toast-cta {
        color: #1ab394;
        font-weight: 600;
        white-space: nowrap;
    }

    .menu-toast-leave-active {
        transition: opacity .3s ease, transform .3s ease;
    }

    .menu-toast-leave-to {
        opacity: 0;
        transform: translateX(30px);
    }

    @media (prefers-reduced-motion: reduce) {
        .menu-toast {
            animation: none !important;
        }

        .menu-toast-leave-active {
            transition: none !important;
        }
    }
</style>
